/**
 * Risk Management API Client
 * Handles risk assessment, limits, monitoring, and compliance
 */

import { BaseApiClient } from './base-client';
import { ApiResponse } from './config';

// Risk Management Types
export interface RiskLimit {
  id: string;
  user_id: string;
  type: 'position_size' | 'daily_loss' | 'portfolio_exposure' | 'concentration';
  symbol?: string;
  limit_value: number;
  current_value: number;
  threshold_warning: number; // percentage of limit that triggers warning
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface RiskAssessment {
  user_id: string;
  overall_risk_score: number; // 0-100
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  assessments: {
    position_concentration: number;
    portfolio_volatility: number;
    leverage_ratio: number;
    drawdown_risk: number;
    correlation_risk: number;
  };
  recommendations: string[];
  timestamp: string;
}

export interface PositionRisk {
  symbol: string;
  position_size: number;
  market_value: number;
  unrealized_pnl: number;
  var_1d: number; // Value at Risk 1 day
  var_5d: number; // Value at Risk 5 days
  beta: number;
  volatility: number;
  risk_contribution: number; // contribution to portfolio risk
  max_position_size: number;
  current_exposure: number;
}

export interface RiskMonitoring {
  user_id: string;
  alerts: RiskAlert[];
  violations: RiskViolation[];
  metrics: {
    portfolio_var: number;
    sharpe_ratio: number;
    max_drawdown: number;
    beta: number;
    correlation_matrix: Record<string, Record<string, number>>;
  };
  last_updated: string;
}

export interface RiskAlert {
  id: string;
  type: 'limit_breach' | 'high_volatility' | 'correlation_spike' | 'drawdown_warning';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  symbol?: string;
  current_value: number;
  threshold_value: number;
  created_at: string;
  acknowledged: boolean;
}

export interface RiskViolation {
  id: string;
  user_id: string;
  limit_id: string;
  violation_type: string;
  violation_value: number;
  limit_value: number;
  symbol?: string;
  created_at: string;
  resolved: boolean;
  resolution_notes?: string;
}

export interface SetRiskLimitRequest {
  type: 'position_size' | 'daily_loss' | 'portfolio_exposure' | 'concentration';
  symbol?: string;
  limit_value: number;
  threshold_warning: number;
}

export interface RiskReportRequest {
  start_date: string;
  end_date: string;
  report_type: 'summary' | 'detailed' | 'violations' | 'performance';
  symbols?: string[];
}

export interface RiskReport {
  report_id: string;
  user_id: string;
  report_type: string;
  period: {
    start_date: string;
    end_date: string;
  };
  summary: {
    total_violations: number;
    avg_risk_score: number;
    max_drawdown: number;
    sharpe_ratio: number;
    total_return: number;
    volatility: number;
  };
  details: any; // Flexible structure based on report type
  generated_at: string;
}

export class RiskManagementApiClient extends BaseApiClient {
  /**
   * Get risk assessment for user
   */
  async getRiskAssessment(userId?: string): Promise<ApiResponse<RiskAssessment>> {
    const endpoint = userId ? `/risk/limits?user_id=${userId}` : '/risk/limits';
    return this.get(endpoint);
  }

  /**
   * Get risk limits for user
   */
  async getRiskLimits(userId?: string): Promise<ApiResponse<RiskLimit[]>> {
    const endpoint = userId ? `/risk/limits?user_id=${userId}` : '/risk/limits';
    return this.get(endpoint);
  }

  /**
   * Set or update risk limit
   */
  async setRiskLimit(data: SetRiskLimitRequest): Promise<ApiResponse<RiskLimit>> {
    return this.post('/risk/limits', data);
  }

  /**
   * Update existing risk limit
   */
  async updateRiskLimit(limitId: string, data: Partial<SetRiskLimitRequest>): Promise<ApiResponse<RiskLimit>> {
    return this.put(`/risk/limits/${limitId}`, data);
  }

  /**
   * Delete risk limit
   */
  async deleteRiskLimit(limitId: string): Promise<ApiResponse<{ message: string }>> {
    return this.delete(`/risk/limits/${limitId}`);
  }

  /**
   * Get position risk analysis
   */
  async getPositionRisk(symbol?: string): Promise<ApiResponse<PositionRisk[]>> {
    const endpoint = symbol ? `/risk/positions?symbol=${symbol}` : '/risk/positions';
    return this.get(endpoint);
  }

  /**
   * Get risk monitoring data
   */
  async getRiskMonitoring(): Promise<ApiResponse<RiskMonitoring>> {
    return this.get('/risk/monitoring');
  }

  /**
   * Get risk alerts
   */
  async getRiskAlerts(acknowledged?: boolean): Promise<ApiResponse<RiskAlert[]>> {
    const endpoint = acknowledged !== undefined
      ? `/risk/alerts?acknowledged=${acknowledged}`
      : '/risk/alerts';
    return this.get(endpoint);
  }

  /**
   * Acknowledge risk alert
   */
  async acknowledgeAlert(alertId: string): Promise<ApiResponse<{ message: string }>> {
    return this.post(`/risk/alerts/${alertId}/acknowledge`);
  }

  /**
   * Get risk violations
   */
  async getRiskViolations(resolved?: boolean): Promise<ApiResponse<RiskViolation[]>> {
    const endpoint = resolved !== undefined
      ? `/risk/violations?resolved=${resolved}`
      : '/risk/violations';
    return this.get(endpoint);
  }

  /**
   * Resolve risk violation
   */
  async resolveViolation(
    violationId: string,
    resolutionNotes: string
  ): Promise<ApiResponse<{ message: string }>> {
    return this.post(`/risk/violations/${violationId}/resolve`, {
      resolution_notes: resolutionNotes
    });
  }

  /**
   * Generate risk report
   */
  async generateRiskReport(data: RiskReportRequest): Promise<ApiResponse<{ report_id: string; status: string }>> {
    return this.post('/risk/reporting', data);
  }

  /**
   * Get risk report
   */
  async getRiskReport(reportId: string): Promise<ApiResponse<RiskReport>> {
    return this.get(`/risk/reporting/${reportId}`);
  }

  /**
   * Get risk reports list
   */
  async getRiskReports(limit: number = 20, offset: number = 0): Promise<ApiResponse<{
    reports: RiskReport[];
    total: number;
    has_more: boolean;
  }>> {
    return this.get(`/risk/reporting?limit=${limit}&offset=${offset}`);
  }

  /**
   * Calculate portfolio VaR (Value at Risk)
   */
  async calculatePortfolioVaR(
    confidence: number = 0.95,
    timeHorizon: number = 1
  ): Promise<ApiResponse<{
    var_amount: number;
    confidence_level: number;
    time_horizon_days: number;
    calculation_method: string;
    timestamp: string;
  }>> {
    return this.post('/risk/var-calculation', {
      confidence_level: confidence,
      time_horizon_days: timeHorizon
    });
  }

  /**
   * Get risk compliance status
   */
  async getComplianceStatus(): Promise<ApiResponse<{
    is_compliant: boolean;
    violations: RiskViolation[];
    warnings: RiskAlert[];
    last_check: string;
  }>> {
    return this.get('/risk/compliance/status');
  }

  /**
   * Run risk stress test
   */
  async runStressTest(scenarios: {
    name: string;
    market_shock: number;
    correlation_change?: number;
    volatility_multiplier?: number;
  }[]): Promise<ApiResponse<{
    test_id: string;
    scenarios: Array<{
      name: string;
      portfolio_impact: number;
      worst_position: string;
      estimated_loss: number;
    }>;
    overall_risk_rating: 'low' | 'medium' | 'high' | 'critical';
  }>> {
    return this.post('/risk/stress-test', { scenarios });
  }
}

